import { Image } from '@tiptap/extension-image'
import { Fragment, Node, Slice } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { ExtendedImageOptions } from './types'

export { type ExtendedImageOptions }

/**
 * Allows remote images only if their domains exist in an allowlist.
 */
const stripPastedImages = (
  fragment: Fragment,
  { allowRemote, allowedDomains }: ExtendedImageOptions
): Fragment => {
  const newNodes: Node[] = []
  fragment.forEach((node) => {
    if (node.type.name === 'image' && node.attrs?.src) {
      const src = new URL(node.attrs.src)
      const domain = src.hostname.match(/[^.]\w+\.[^.]\w+$/)?.[0]
      if (!allowRemote || (domain && allowedDomains?.includes(domain))) {
        console.warn(`Stripping forbidden image from ${src.hostname}`)
        return
      }
    }

    newNodes.push(node)
  })

  return Fragment.fromArray(newNodes)
}

/**
 * Replaces <img> tags with an emoji alt attribute with the emoji.
 * Useful when pasting text from twitter.com, where they render emoji
 * as SVG images.
 *
 * @param html An HTML snippet containing <img> tags with alt attributes.
 */
const replaceEmojiFromImages = (html: string): string => {
  if (!html) {
    return ''
  }

  const emojiRe = new RegExp(
    /\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]*/
  )
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const imagesWithAltAttrs =
    doc.body.querySelectorAll<HTMLImageElement>('img[alt]')
  imagesWithAltAttrs.forEach((image) => {
    if (emojiRe.test(image.alt)) {
      image.replaceWith(image.alt)
    }
  })

  return doc.body.innerHTML
}

export const ExtendedImage = Image.extend<ExtendedImageOptions>({
  addOptions() {
    return {
      allowBase64: false,
      HTMLAttributes: {},
      inline: false,
      resize: false,

      // NOTE: `...this.parent?.()` is here so
      // the defaults above can be overridden.
      ...this.parent?.(),
      allowRemote: true,
      allowedDomains: [],
    }
  },

  addProseMirrorPlugins() {
    const options = this.options

    return [
      new Plugin({
        key: new PluginKey('ExtendedImage'),
        props: {
          transformPastedHTML(html) {
            return replaceEmojiFromImages(html)
          },
          transformPasted(slice) {
            return new Slice(
              stripPastedImages(slice.content, options),
              slice.openStart,
              slice.openEnd
            )
          },
        },
      }),
    ]
  },
})
